type AndroidMethod = (...args: unknown[]) => unknown

export type AndroidResponseEnvelope<Result> =
  | {
      success: true
      result: Result
    }
  | {
      success: false
      error: {
        code: string
        message: string
      }
    }

interface AndroidBridgeInterface {
  [methodName: string]: AndroidMethod | undefined
}

declare global {
  interface Window {
    Android?: AndroidBridgeInterface
    _callbacks?: Record<string, (_responseJson: string) => void>
    onResult?: (_callbackId: string, _responseJson: string) => void
  }
}

const BRIDGE_TIMEOUT_MS = 15_000

const buildErrorFromEnvelope = (error: { code: string; message: string }) => new Error(`[${error.code}] ${error.message}`)

const ensureBridgeCallbacks = () => {
  window._callbacks = window._callbacks ?? {}

  if (!window.onResult) {
    window.onResult = (callbackId, responseJson) => {
      const callback = window._callbacks?.[callbackId]
      if (!callback) {
        console.warn(`[bridge] onResult received for unknown callbackId=${callbackId}`)
        return
      }

      callback(responseJson)
      delete window._callbacks?.[callbackId]
    }
  }
}

const generateCallbackId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return `cb_${Date.now()}_${Math.random().toString(16).slice(2)}`
}

const serializePayload = (payload: unknown) => {
  if (typeof payload === 'string' || typeof payload === 'number' || typeof payload === 'boolean') {
    return payload
  }

  return JSON.stringify(payload)
}

const parseEnvelope = <Result>(responseJson: string): AndroidResponseEnvelope<Result> => {
  const parsedResponse = JSON.parse(responseJson) as unknown

  if (!parsedResponse || typeof parsedResponse !== 'object' || Array.isArray(parsedResponse)) {
    throw new Error('Android returned an invalid response envelope.')
  }

  return parsedResponse as AndroidResponseEnvelope<Result>
}

export const invokeAndroidVoid = (methodName: string): void => {
  if (typeof window.Android?.[methodName] !== 'function') {
    console.warn(`[bridge] Android.${methodName} is unavailable.`)
    return
  }
  try {
    // WebView's JS bridge requires the method to be invoked through property
    // access on the injected object — calling a saved reference throws
    // "Java bridge method can't be invoked on a non-injected object".
    window.Android[methodName]!()
  } catch (error) {
    console.warn(`[bridge] Android.${methodName} threw:`, error)
  }
}

export const openWawelDiagnostics = (): void => invokeAndroidVoid('openWawelDiagnostics')

export const invokeAndroidMethod = <Result>(methodName: string, payload?: unknown) =>
  new Promise<Result>((resolve, reject) => {
    const t0 = Date.now()
    ensureBridgeCallbacks()

    const androidMethod = window.Android?.[methodName]
    if (typeof androidMethod !== 'function') {
      reject(new Error(`Android.${methodName} is unavailable.`))
      return
    }

    const callbackId = generateCallbackId()
    let settled = false

    const timeoutId = setTimeout(() => {
      if (settled) return
      settled = true
      delete window._callbacks?.[callbackId]
      reject(new Error(`Android.${methodName} timed out.`))
    }, BRIDGE_TIMEOUT_MS)

    window._callbacks![callbackId] = responseJson => {
      if (settled) return
      settled = true
      clearTimeout(timeoutId)

      const elapsed = Date.now() - t0
      try {
        const envelope = parseEnvelope<Result>(responseJson)

        if (envelope.success) {
          console.log(`[bridge] ${methodName} OK in ${elapsed}ms cbId=${callbackId}`)
          resolve(envelope.result)
          return
        }

        reject(buildErrorFromEnvelope(envelope.error))
      } catch (error) {
        reject(error)
      }
    }

    try {
      if (payload !== undefined) {
        window.Android![methodName]!(callbackId, serializePayload(payload))
      } else {
        window.Android![methodName]!(callbackId)
      }
    } catch (error) {
      if (settled) return
      settled = true
      clearTimeout(timeoutId)
      delete window._callbacks?.[callbackId]
      reject(error)
    }
  })
